import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, PencilSimple, X, EnvelopeSimple, ArrowsClockwise, Package, Truck, MapPin } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput, BrutalTextarea } from "../components/Brutal";
import { ChargeAccountDialog } from "../components/AccountCharges";
import { PartnerOrdersSection } from "../components/PartnerOrders";
import { ShortfallCard } from "../components/ShortfallCard";
import { useAuth } from "../AuthContext";

/**
 * v406.0 — Store Orders admin console. Every order placed on a distributor's
 * public AXIS store, with GOD MODE editing for owner/admin: fix customer
 * details, totals, flip status (paid triggers the customer receipt email),
 * cancel (restores auto-deducted stock), and resend receipts.
 */
export default function StoreOrdersPage() {
  const { t, i18n } = useTranslation();
  const { user, isSuperOwner } = useAuth();
  const [data, setData] = useState({ items: [], counts: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [shippingTarget, setShippingTarget] = useState(null); // v407.1 one-tap shipping charge
  const [shipTarget, setShipTarget] = useState(null); // v408.4 mark-shipped dialog (optional tracking)
  const [busyId, setBusyId] = useState(null);

  const canGod = ["owner", "admin"].includes(user?.role) || isSuperOwner;
  const isDistributor = user?.role === "distributor";

  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US",
      { year: "numeric", month: "short", day: "numeric" }) : "—";

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/store-orders", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const patchStatus = async (o, status, confirmMsg, paidMethod, cashCollectedBy) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(o.id);
    try {
      const { data } = await api.patch(`/store-orders/${o.id}`, {
        status,
        ...(paidMethod ? { paid_method: paidMethod } : {}),
        ...(cashCollectedBy ? { cash_collected_by: cashCollectedBy } : {}),
      });
      if (status === "paid") {
        toast.success(data.receipt?.ok
          ? t("store_orders.paid_receipt_sent", { email: data.row?.customer_email })
          : t("store_orders.marked_paid"));
      } else {
        toast.success(t("common.saved"));
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const markShipped = async (o, trackingNumber) => {
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/store-orders/${o.id}/mark-shipped`,
        { tracking_number: trackingNumber || null });
      toast.success(data.customer_notified
        ? t("store_orders.shipped_customer_notified", { email: data.row?.customer_email })
        : t("store_orders.marked_shipped"));
      setShipTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const resendReceipt = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/store-orders/${o.id}/send-receipt`);
      toast.success(t("store_orders.receipt_resent", { email: data.sent_to }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const sendReminder = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/store-orders/${o.id}/send-reminder`);
      toast.success(t("store_orders.reminder_sent", { email: data.sent_to }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const statusPill = (s) => {
    const cls = {
      awaiting_payment: "bg-amber-50 text-amber-700 border-amber-200",
      paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      shipped: "bg-sky-50 text-sky-700 border-sky-200",
      cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
    }[s] || "bg-zinc-100 text-zinc-700 border-zinc-200";
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 border rounded-full ${cls}`} data-testid={`order-status-${s}`}>
        {t(`store_orders.status_${s}`)}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="store-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-1">
            <ArrowLeft size={12} weight="bold" /> {t("common.back")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("store_orders.title")}</h1>
          <p className="text-sm text-zinc-600 mt-0.5">{t("store_orders.subtitle")}</p>
        </div>
      </div>

      {/* v410.5 — Active Bio Labs partner console: ABOVE the store orders so
          it's findable on mobile (was buried under every order card) */}
      {canGod && <PartnerOrdersSection />}
      {canGod && <ShortfallCard />}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["awaiting_payment", "paid", "shipped", "cancelled"].map((key) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? "" : key)}
            className={`text-left border rounded-xl p-4 transition ${
              filter === key ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white hover:bg-zinc-50"
            }`}
            data-testid={`orders-tile-${key}`}
          >
            <div className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">
              {t(`store_orders.status_${key}`)}
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-xl font-semibold tabular-nums">{fmt$(data.totals?.[key] || 0)}</span>
              <span className="text-xs text-zinc-500">{t("store_orders.count", { n: data.counts?.[key] || 0 })}</span>
            </div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md p-4">{t("common.loading")}</div>
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="store-orders-empty">
          <p className="text-sm font-medium text-zinc-900">{t("store_orders.empty")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("store_orders.empty_hint")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`}
              className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-bold text-[13px] bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-md px-2 py-0.5" data-testid={`order-ref-${o.id}`}>
                    {o.reference}
                  </span>
                  {statusPill(o.status)}
                  {o.paid_method === "cash" && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-300" data-testid={`order-cash-${o.id}`}>
                      {t("store_orders.cash_badge")}
                    </span>
                  )}
                  {o.delivery_method === "pickup" && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-300" data-testid={`order-pickup-${o.id}`}>
                      {t("store_orders.pickup_badge")}
                    </span>
                  )}
                  {o.amount_mismatch && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200" data-testid={`order-mismatch-${o.id}`}>
                      {t("store_orders.mismatch")}
                    </span>
                  )}
                  {o.edited && (
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 inline-flex items-center gap-1"
                      title={t("store_orders.edited_by", { who: o.edited_by_name || o.edited_by_email || "", when: fmtDate(o.edited_at) })}
                      data-testid={`order-edited-badge-${o.id}`}
                    >
                      <PencilSimple size={10} weight="bold" /> {t("store_orders.edited_badge")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {fmtDate(o.created_at)} · <span className="font-medium text-zinc-700">{o.distributor_name}</span> ({o.distributor_code})
                  {o.paid_at && <> · {t("store_orders.paid_on", { date: fmtDate(o.paid_at) })}</>}
                  {o.paid_by_name && <> · {t("store_orders.paid_by", { name: o.paid_by_name })}</>}
                </div>
                <div className="text-xs text-zinc-700 mt-1">
                  <span className="font-semibold">{o.customer_name}</span>
                  {o.customer_email
                    ? <> · <span data-testid={`order-email-${o.id}`}>{o.customer_email}</span></>
                    : <> · <span className="text-zinc-400 italic">{t("store_orders.no_email")}</span></>}
                  {o.customer_contact && <> · {o.customer_contact}</>}
                </div>
                <div className="text-xs text-zinc-600 mt-1" data-testid={`order-items-${o.id}`}>
                  {(o.items || []).map((it) => `${it.quantity}x ${it.name}`).join(" · ")}
                </div>
                {o.shipping_address ? (
                  <div className="text-xs text-zinc-700 mt-1 inline-flex items-start gap-1" data-testid={`order-address-${o.id}`}>
                    <MapPin size={13} weight="bold" className="shrink-0 mt-0.5 text-[#8a6a1c]" />
                    <span className="font-medium">{o.shipping_address}</span>
                  </div>
                ) : (
                  o.payment_method === "etransfer" && o.delivery_method !== "pickup" && o.status !== "cancelled" && (
                    <div className="text-[11px] text-rose-600 mt-1" data-testid={`order-no-address-${o.id}`}>
                      {t("store_orders.no_address")}
                    </div>
                  )
                )}
                {o.shipped_at && (
                  <div className="text-[11px] text-sky-700 mt-1 inline-flex items-center gap-1" data-testid={`order-shipped-info-${o.id}`}>
                    <Truck size={12} weight="bold" /> {t("store_orders.shipped_on", { date: fmtDate(o.shipped_at), who: o.shipped_by_name || "" })}
                    {o.tracking_number && (
                      <span className="font-mono font-semibold" data-testid={`order-tracking-${o.id}`}>
                        · {o.tracking_number}
                      </span>
                    )}
                  </div>
                )}
                {o.receipt_sent_at && (
                  <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1" data-testid={`order-receipt-sent-${o.id}`}>
                    <EnvelopeSimple size={12} weight="bold" /> {t("store_orders.receipt_sent_to", { email: o.receipt_sent_to, date: fmtDate(o.receipt_sent_at) })}
                  </div>
                )}
                {o.last_reminder_at && o.status === "awaiting_payment" && (
                  <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1" data-testid={`order-reminded-info-${o.id}`}>
                    <EnvelopeSimple size={12} weight="bold" /> {t("store_orders.reminded_on", { date: fmtDate(o.last_reminder_at), n: o.reminder_count || 1 })}
                  </div>
                )}
                {o.note && <div className="text-xs italic text-zinc-500 mt-1 truncate" title={o.note}>&ldquo;{o.note}&rdquo;</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <div className="text-right mr-1">
                  <div className="text-lg font-semibold tabular-nums text-zinc-900">{fmt$(o.total)}</div>
                  {o.shipping_fee > 0 && (
                    <div className="text-[10px] text-zinc-400" data-testid={`order-shipping-${o.id}`}>
                      incl. {fmt$(o.shipping_fee)} shipping
                    </div>
                  )}
                  {o.concierge_fee > 0 && (
                    <div className="text-[10px] font-semibold text-[#8a6a1c]" data-testid={`order-concierge-${o.id}`}>
                      incl. {fmt$(o.concierge_fee)} concierge ({o.concierge_vials} vials)
                    </div>
                  )}
                  {o.discount_amount > 0 && (
                    <div className="text-[10px] font-semibold text-emerald-700" data-testid={`order-discount-${o.id}`}>
                      −{fmt$(o.discount_amount)} ({o.discount_code})
                    </div>
                  )}
                </div>
                {canGod && o.status === "awaiting_payment" && (
                  <>
                    <BrutalButton size="sm" disabled={busyId === o.id}
                      onClick={() => patchStatus(o, "paid", t("store_orders.mark_paid_confirm", { ref: o.reference }), "etransfer")}
                      data-testid={`order-mark-paid-${o.id}`}>
                      <CheckCircle size={12} weight="bold" /> {t("store_orders.mark_paid")}
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" disabled={busyId === o.id}
                      onClick={() => {
                        if (!window.confirm(t("store_orders.mark_paid_cash_confirm", { ref: o.reference }))) return;
                        const houseCollected = window.confirm(t("store_orders.cash_who_confirm"));
                        patchStatus(o, "paid", null, "cash", houseCollected ? "house" : "distributor");
                      }}
                      data-testid={`order-mark-paid-cash-${o.id}`}
                      className="!border-emerald-300 !text-emerald-800 hover:!bg-emerald-50">
                      <CheckCircle size={12} weight="bold" /> {t("store_orders.mark_paid_cash")}
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" disabled={busyId === o.id}
                      onClick={() => patchStatus(o, "cancelled", t("store_orders.cancel_confirm", { ref: o.reference }))}
                      data-testid={`order-cancel-${o.id}`}
                      className="!border-rose-300 !text-rose-700 hover:!bg-rose-50">
                      <X size={12} weight="bold" /> {t("store_orders.cancel_order")}
                    </BrutalButton>
                  </>
                )}
                {(canGod || isDistributor) && o.status === "awaiting_payment" && o.customer_email && (
                  <BrutalButton size="sm" variant="secondary" disabled={busyId === o.id}
                    onClick={() => sendReminder(o)}
                    data-testid={`order-send-reminder-${o.id}`}
                    className="!border-amber-300 !text-amber-800 hover:!bg-amber-50">
                    <EnvelopeSimple size={12} weight="bold" /> {t("store_orders.remind")}
                  </BrutalButton>
                )}
                {(canGod || isDistributor) &&
                  (o.status === "paid" ||
                    (o.status === "awaiting_payment" && o.payment_method === "arrange")) && (
                  <BrutalButton size="sm" disabled={busyId === o.id}
                    onClick={() => setShipTarget(o)}
                    data-testid={`order-mark-shipped-${o.id}`}
                    className="!bg-sky-600 !border-sky-600 hover:!bg-sky-700">
                    <Truck size={12} weight="bold" /> {t("store_orders.mark_shipped")}
                  </BrutalButton>
                )}
                {canGod && o.status === "paid" && o.customer_email && (
                  <BrutalButton size="sm" variant="secondary" disabled={busyId === o.id}
                    onClick={() => resendReceipt(o)} data-testid={`order-resend-receipt-${o.id}`}>
                    <EnvelopeSimple size={12} weight="bold" /> {t("store_orders.resend_receipt")}
                  </BrutalButton>
                )}
                {canGod && (o.status === "paid" || o.status === "shipped" || o.status === "cancelled") && (
                  <BrutalButton size="sm" variant="secondary" disabled={busyId === o.id}
                    onClick={() => patchStatus(o, "awaiting_payment", t("store_orders.reopen_confirm", { ref: o.reference }))}
                    data-testid={`order-reopen-${o.id}`} title={t("store_orders.reopen")}>
                    <ArrowsClockwise size={12} weight="bold" />
                  </BrutalButton>
                )}
                {canGod && o.status !== "cancelled" && (
                  <BrutalButton size="sm" variant="secondary" onClick={() => setShippingTarget(o)}
                    data-testid={`order-shipping-charge-${o.id}`} title={t("store_orders.add_shipping_cost")}
                    className="!border-sky-300 !text-sky-700 hover:!bg-sky-50">
                    <Package size={12} weight="bold" />
                  </BrutalButton>
                )}
                {canGod && o.status !== "cancelled" && (
                  <BrutalButton size="sm" variant="secondary" onClick={() => setEditTarget(o)}
                    data-testid={`order-edit-${o.id}`} title={t("store_orders.edit")}>
                    <PencilSimple size={12} weight="bold" />
                  </BrutalButton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editTarget && (
        <EditOrderDialog
          order={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
          t={t}
        />
      )}

      {/* v408.4 — mark shipped with optional tracking number */}
      {shipTarget && (
        <MarkShippedDialog
          order={shipTarget}
          busy={busyId === shipTarget.id}
          onClose={() => setShipTarget(null)}
          onConfirm={(tracking) => markShipped(shipTarget, tracking)}
          t={t}
        />
      )}

      {/* v407.1 — shipping cost charged to the distributor's account, prefilled from the order */}
      {shippingTarget && (
        <ChargeAccountDialog
          distributorId={shippingTarget.distributor_id}
          distributorName={shippingTarget.distributor_name}
          defaultCategory="shipping"
          defaultReason={`Shipping — ${shippingTarget.reference}`}
          orderReference={shippingTarget.reference}
          storeOrderId={shippingTarget.id}
          onClose={() => setShippingTarget(null)}
          onCreated={() => setShippingTarget(null)}
        />
      )}
    </div>
  );
}

function MarkShippedDialog({ order, busy, onClose, onConfirm, t }) {
  const [tracking, setTracking] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onConfirm(tracking.trim()); }}
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3"
        data-testid="mark-shipped-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight inline-flex items-center gap-2">
          <Truck size={20} weight="bold" className="text-sky-600" />
          {t("store_orders.ship_dialog_title", { ref: order.reference })}
        </h2>
        <p className="text-sm text-zinc-600">{t("store_orders.ship_dialog_hint")}</p>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("store_orders.tracking_label")}</label>
          <BrutalInput
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder={t("store_orders.tracking_placeholder")}
            autoFocus
            data-testid="ship-tracking-input"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1 !bg-sky-600 !border-sky-600 hover:!bg-sky-700"
            disabled={busy} data-testid="ship-confirm-btn">
            {busy ? "…" : t("store_orders.mark_shipped")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}

function EditOrderDialog({ order, onClose, onSaved, t }) {
  const [form, setForm] = useState({
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    customer_contact: order.customer_contact || "",
    shipping_address: order.shipping_address || "",
    total: String(order.total ?? ""),
    note: order.note || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/store-orders/${order.id}`, {
        customer_name: form.customer_name.trim() || null,
        customer_email: form.customer_email.trim() || null,
        customer_contact: form.customer_contact,
        shipping_address: form.shipping_address,
        total: form.total === "" ? null : Number(form.total),
        note: form.note,
      });
      toast.success(t("common.saved"));
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="order-edit-dialog"
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <h2 className="font-semibold text-lg">{t("store_orders.edit_title")}</h2>
        <p className="text-xs text-zinc-500 font-mono">{order.reference}</p>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("store_orders.customer_name")}</label>
          <BrutalInput value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            data-testid="order-edit-name" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("store_orders.customer_email")}</label>
          <BrutalInput type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            data-testid="order-edit-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">{t("store_orders.customer_contact")}</label>
            <BrutalInput value={form.customer_contact} onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
              data-testid="order-edit-contact" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">{t("store_orders.total")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={form.total}
              onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="order-edit-total" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("store_orders.shipping_address")}</label>
          <BrutalTextarea rows={2} value={form.shipping_address}
            onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
            data-testid="order-edit-address" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("store_orders.note")}</label>
          <BrutalTextarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="order-edit-note" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <BrutalButton type="button" variant="secondary" onClick={onClose} data-testid="order-edit-cancel">
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" disabled={busy} data-testid="order-edit-save">
            {busy ? t("common.saving") : t("common.save")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}
